<template>
  <chart :option="calOptions" ref="chart" class="chart-cont"></chart>
</template>

<script>
import lodash, { merge } from 'lodash';
import numeral from 'numeral';
import dayjs from 'dayjs';
import Chart from '@/components/echarts/chart.vue';
import { optionGen } from '@/utils/helper';
import { DEFAULT_TIME_UNIT_ENUM } from '@/views/analysis/retention-analysis/components/config';

export default {
  name: 'DistributeDataChart',
  components: {
    Chart,
  },
  props: {
    analysisData: {
      type: Object,
    },
    chartType: {
      type: String,
    },
    chartShowType: {
      type: String,
    },
  },
  data() {
    return {
      xAxisData: [],
      seriesData: [],
      legendData: [],
    };
  },
  computed: {
    calOptions() {
      const options = optionGen();

      merge(options, {
        legend: {
          show: true,
          data: this.legendData,
        },
        tooltip: {
          show: true,
          formatter: params => {
            const { name } = params[0];
            let str = `${name}`;
            params.forEach(({ marker, seriesName, data }) => {
              str += `</br>${marker} ${seriesName}： ${
                this.chartShowType === 'ratioLine' ? this.ratioFormat(data) : this.numFormat(data)
              }`;
            });
            return str;
          },
        },
        xAxis: { type: 'category', data: this.xAxisData },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: value => {
              return this.chartType === 'trend' && this.chartShowType === 'ratioLine'
                ? this.ratioFormat(value)
                : this.numFormat(value);
            },
          },
        },
        grid: {
          top: 15,
          left: '1%',
          right: '1%',
          bottom: 30,
          containLabel: true,
        },
        series: this.seriesData,
      });

      return options;
    },
  },
  watch: {
    analysisData: {
      handler() {
        this.translateAnalysisData();
      },
      deep: true,
    },
    chartShowType: {
      handler() {
        this.translateAnalysisData();
      },
      deep: true,
    },
  },
  mounted() {
    this.translateAnalysisData();
  },
  methods: {
    translateAnalysisData() {
      this.legendData = this.getLegendData();
      this.xAxisData = this.getXAxisData();
      this.seriesData = this.getSeriesData();
    },
    getLegendData() {
      const legendData = [];
      const { chartType, analysisData } = this;
      const { dataItemList } = analysisData;

      if (chartType === 'trend') {
        dataItemList.forEach(dataItem => {
          legendData.push(
            this.getSeriesName('A1', dataItem.globalFilterName, dataItem.groupByKey, dataItem.distributionScope),
          );
        });
      } else {
        const groupAnalysisDataMap = this.translateGroupAnalysisData();
        Object.keys(groupAnalysisDataMap).forEach((key, index) => {
          const arr = groupAnalysisDataMap[key];

          const dataArr = [];
          analysisData.distributionScopeList.forEach((scope, subIndex) => {
            const dataItem = arr.find(item => item.distributionScope === scope) || { dataSum: 0 };
            dataArr.push(dataItem.dataSum);
          });

          const firstItem = arr && arr[0] ? arr[0] : { globalFilterName: '', groupByKey: '' };

          legendData.push(this.getSeriesName('', firstItem.globalFilterName, firstItem.groupByKey, ''));
        });
      }

      return legendData.slice(0, 10);
    },
    getXAxisData() {
      const xAxisData = [];
      const { chartType, analysisData } = this;
      const { dateList, distributionScopeList } = analysisData;
      const fieldList = chartType === 'trend' ? dateList : distributionScopeList;

      fieldList.forEach(item => {
        xAxisData.push(`${item}`);
      });
      return xAxisData;
    },
    getSeriesData() {
      const { chartType, chartShowType, analysisData } = this;
      const { dataItemList } = analysisData;

      const seriesData = [];

      if (chartType === 'trend' && chartShowType === 'line') {
        dataItemList.forEach(dataItem => {
          seriesData.push({
            name: this.getSeriesName('A1', dataItem.globalFilterName, dataItem.groupByKey, dataItem.distributionScope),
            type: 'line',
            data: dataItem.data,
          });
        });
      }
      if (chartType === 'trend' && chartShowType === 'ratioLine') {
        const sumDataArr = [];
        dataItemList.forEach(dataItem => {
          dataItem.data.forEach((subDataItem, index) => {
            const sum = (sumDataArr[index] || 0) + subDataItem;
            sumDataArr[index] = sum;
          });
        });

        dataItemList.forEach(dataItem => {
          const ratioDataArr = [];
          dataItem.data.forEach((subDataItem, index) => {
            const ratio = sumDataArr[index] ? subDataItem / sumDataArr[index] : 0;
            ratioDataArr.push(ratio);
          });
          seriesData.push({
            name: this.getSeriesName('A1', dataItem.globalFilterName, dataItem.groupByKey, dataItem.distributionScope),
            type: 'line',
            data: ratioDataArr,
          });
        });
      }
      if (chartType === 'group') {
        const groupAnalysisDataMap = this.translateGroupAnalysisData();
        Object.keys(groupAnalysisDataMap).forEach((key, index) => {
          const arr = groupAnalysisDataMap[key];

          const dataArr = [];
          analysisData.distributionScopeList.forEach((scope, subIndex) => {
            const dataItem = arr.find(item => item.distributionScope === scope) || { dataSum: 0 };
            dataArr.push(dataItem.dataSum);
          });

          const firstItem = arr && arr[0] ? arr[0] : { globalFilterName: '', groupByKey: '' };

          console.log(firstItem.globalFilterName, firstItem.groupByKey);
          seriesData.push({
            name: this.getSeriesName('', firstItem.globalFilterName, firstItem.groupByKey, ''),
            type: 'bar',
            stack: 'one',
            data: dataArr,
          });
        });
      }

      return seriesData.slice(0, 10);
    },
    translateGroupAnalysisData() {
      const groupAnalysisDataMap = {};
      this.analysisData.dataItemList.forEach(dataItem => {
        const key = `${dataItem.globalFilterName}-${dataItem.groupByKey}`;
        let arr = groupAnalysisDataMap[key];
        if (!arr) {
          arr = [];
          groupAnalysisDataMap[key] = arr;
        }
        arr.push(dataItem);
      });
      return groupAnalysisDataMap;
    },
    getSeriesName(prefix, globalFilterName, groupByKey, name) {
      const arr = [];
      if (globalFilterName) {
        arr.push(globalFilterName);
      }
      arr.push(groupByKey && groupByKey !== 'ALL' ? `${groupByKey}` : '总体');
      if (name) {
        arr.push(name);
      }
      return (prefix ? `${prefix} ` : prefix) + arr.join(',');
    },
    numFormat(num) {
      return numeral(num).format('0,0');
    },
    ratioFormat(num, format = '0.00%') {
      return numeral(num).format(format);
    },
  },
};
</script>

<style scoped lang="less">
.chart-cont {
  width: 100%;
  height: 100%;
}
</style>
